import React, { useMemo, useState } from 'react';
import { Button, Card, Pill, Progress } from '../components/Primitives';
import { Icon } from '../components/Icon';
import { useApp } from '../lib/app-context';
import { canAccess, deterministicShuffle, dynamicLesson, weeklyBossDay } from '../lib/learning';
import { grammarNote } from '../content/grammar';
import { queueLearningEvent } from '../lib/analytics';

function normalize(value: string) {
  return value.trim().toLowerCase().replace(/[’‘`]/g, "'").replace(/\s+/g, ' ');
}

export function Boss() {
  const { state, answer, answerSkill, setRoute } = useApp();
  const allowed = canAccess(state, 'boss');
  // Freeze the whole challenge at mount. Learning answers update global state, but must never
  // reorder or replace questions while the learner is in the middle of a Boss session.
  const [lesson] = useState(() => dynamicLesson(state));
  const [words] = useState(() => deterministicShuffle(
    [...lesson.newWords, ...lesson.reviewWords],
    `boss-${state.progress.day}-${state.progress.courseDate ?? ''}`,
  ).slice(0, 8));
  const note = grammarNote(lesson.grammar);
  const recognitionCount = Math.min(4, words.length);
  const totalQuestions = words.length + 1;

  const [index, setIndex] = useState(0);
  const [picked, setPicked] = useState<string | null>(null);
  const [typed, setTyped] = useState('');
  const [typedResult, setTypedResult] = useState<boolean | null>(null);
  const [correct, setCorrect] = useState(0);
  const [grammarDone, setGrammarDone] = useState(false);
  const [finished, setFinished] = useState(false);
  const word = words[index];
  const productionMode = index >= recognitionCount;

  const options = useMemo(() => word
    ? deterministicShuffle(
      [word.uz, ...words.filter((item) => item.id !== word.id).slice(0, 3).map((item) => item.uz)],
      `boss-opt-${word.id}`,
    )
    : [], [word, words]);

  const recordWord = (ok: boolean) => {
    if (!word) return;
    answer(word, ok, 0, productionMode ? 'form_recall' : 'meaning_recall');
    if (ok) setCorrect((value) => value + 1);
  };

  const choose = (opt: string) => {
    if (!word || picked || productionMode) return;
    setPicked(opt);
    recordWord(opt === word.uz);
  };

  const submitTyped = () => {
    if (!word || !productionMode || typedResult !== null) return;
    const ok = normalize(typed) === normalize(word.en);
    setTypedResult(ok);
    recordWord(ok);
  };

  const next = () => {
    setPicked(null);
    setTyped('');
    setTypedResult(null);
    if (index < words.length - 1) setIndex((value) => value + 1);
    else setGrammarDone(true);
  };

  const finishGrammar = (opt: string) => {
    if (finished) return;
    const ok = opt === note.answer;
    answerSkill('grammar', ok, note.title);
    const finalCorrect = correct + (ok ? 1 : 0);
    const score = Math.round((finalCorrect / Math.max(1, totalQuestions)) * 100);
    setCorrect(finalCorrect);
    setFinished(true);
    void queueLearningEvent({
      type: 'boss_complete',
      day: state.progress.day,
      courseDate: state.progress.courseDate,
      score,
      passed: score >= 75,
    });
  };

  if (!allowed) {
    return <div className="screen"><Card className="locked-card"><Icon name="lock" size={38}/><h2>Boss Battle — PRO</h2><p>Haftalik aralash sinov trial yoki PRO bilan ochiladi.</p><Button onClick={() => setRoute('pricing')}>PRO</Button></Card></div>;
  }

  const score = finished
    ? Math.round((correct / Math.max(1, totalQuestions)) * 100)
    : Math.round((correct / Math.max(1, Math.min(index + 1, words.length))) * 100);

  return <div className="screen">
    <div className="screen-head"><div><span className="eyebrow">HAFTALIK INTEGRATED CHALLENGE</span><h1>Boss Battle</h1><p>Ma’no eslash + yozib topish + grammatika. Savollar sessiya davomida o‘zgarmaydi.</p></div><Pill tone="gold">KEYINGI BOSS: DAY {weeklyBossDay(state.progress.day)}</Pill></div>

    {!grammarDone && word && !productionMode && <Card className="lesson-stage quiz-stage">
      <div className="word-progress"><span>{index + 1}/{totalQuestions}</span><Pill tone="gold">MEANING RECALL</Pill></div>
      <Progress value={(index / totalQuestions) * 100}/>
      <h1>{word.en}</h1>
      <div className="quiz-options">{options.map((opt) => {
        const cls = picked ? (opt === word.uz ? 'correct' : opt === picked ? 'wrong' : '') : '';
        return <button key={opt} className={cls} disabled={Boolean(picked)} onClick={() => choose(opt)}>{opt}</button>;
      })}</div>
      {picked && <Button onClick={next}>Keyingi</Button>}
    </Card>}

    {!grammarDone && word && productionMode && <Card className="lesson-stage quiz-stage">
      <div className="word-progress"><span>{index + 1}/{totalQuestions}</span><Pill tone="blue">FORM RECALL</Pill></div>
      <Progress value={(index / totalQuestions) * 100}/>
      <span className="eyebrow">O‘ZBEKCHADAN INGLIZCHAGA</span>
      <h2>{word.uz}</h2>
      <input
        className="production-input"
        value={typed}
        autoComplete="off"
        autoCapitalize="none"
        disabled={typedResult !== null}
        placeholder="Inglizcha javobni yozing"
        onChange={(event) => setTyped(event.target.value)}
        onKeyDown={(event) => { if (event.key === 'Enter') submitTyped(); }}
      />
      {typedResult === null
        ? <Button disabled={!typed.trim()} onClick={submitTyped}>Tekshirish</Button>
        : <><div className={`production-feedback ${typedResult ? 'correct' : 'wrong'}`}><strong>{typedResult ? 'To‘g‘ri' : 'Qayta eslab qoling'}</strong>{!typedResult && <span>To‘g‘ri javob: <b>{word.en}</b></span>}</div><Button onClick={next}>Keyingi</Button></>}
    </Card>}

    {grammarDone && !finished && <Card className="lesson-stage quiz-stage">
      <Pill tone="blue">FINAL GRAMMAR</Pill>
      <h2>{note.question}</h2>
      <div className="quiz-options">{deterministicShuffle(note.options, `boss-grammar-${state.progress.day}`).map((opt) => <button key={opt} onClick={() => finishGrammar(opt)}>{opt}</button>)}</div>
    </Card>}

    {finished && <Card className="lesson-stage result-stage">
      <div className={`score-ring ${score >= 75 ? 'pass' : 'fail'}`}><strong>{score}%</strong><span>Boss</span></div>
      <h1>{score >= 75 ? 'Boss yengildi' : 'Rebuild kerak'}</h1>
      <p>{score >= 75 ? 'Haftalik aralash recall muvaffaqiyatli.' : 'Zaif bilimlar Daily Mission va Memory Forge’da qayta keladi.'}</p>
      <Button onClick={() => setRoute('knowledge')}>Bilim holati</Button>
    </Card>}
  </div>;
}
